import React, {ChangeEvent, useState} from 'react';
import {Nil} from '../model/SharedTypes';
import NxGenericTextField from '../nxGenericTextField/NxGenericTextField';
import {isNaN, isNil} from 'lodash/fp';
import {logMissingOnChange} from '../inputError';
import {InputEndAdornment} from './InputEndAdornment';

export interface NxNumberInputProps {
  className?: string;
  disabled?: boolean;
  endAdornment?: React.ReactNode;
  error?: string;
  id?: string;
  label: React.ReactNode;
  name?: string;
  onBlur?: () => void;
  onChange?: (value: number | null) => void;
  positionAbsoluteError?: boolean;
  min?: number;
  max?: number;
  step?: number;
  value?: number | null;
  showArrows?: boolean;
  required?: boolean;
  inputComponent?: React.ElementType<{
    inputRef: (ref: HTMLInputElement | null) => void;
  }>
}

const digitsAfterComma = (step: number): number => Math.max(0, -Math.log10(step));
const formatNumber = (value: number, step: number): string => value.toFixed(digitsAfterComma(step));

const calculateTextValue = (value: number | Nil, lastValue: string, step: number): string => {
  if (value === undefined) {
    return '';
  }

  if (value === null) {
    return lastValue;
  }

  if (isNaN(value)) {
    return lastValue;
  }

  if(Number(lastValue) !== value) {
    return formatNumber(value, step);
  }

  return lastValue;
}

export const isValidNumber = (text: string): boolean => /^-?(:?[0-9]?|[1-9][0-9]*)(\.[0-9]+)?$/.test(text);

// undefined clears input
const NxNumberInput = (
    {
      className,
      disabled = false,
      endAdornment,
      error,
      id,
      label,
      min,
      max,
      name,
      onBlur,
      onChange,
      positionAbsoluteError,
      required,
      step = 1,
      value,
      showArrows = true,
      inputComponent
    }: NxNumberInputProps
  ): React.ReactElement => {

  const [lastValue, setLastValue] = useState(isNil(value) ? '' : String(value));
  const [previousValue, setPreviousValue] = useState(value);

  const didValueChange = previousValue !== value && !(isNaN(previousValue) && isNaN(value));
  const textValue = calculateTextValue(didValueChange ? value : Number(lastValue), lastValue, step);
  if(didValueChange) {
    setPreviousValue(value);
  }

  const updateNextValue = (multiplier: number): void => {
    let nextValue = getNextValue(multiplier);

    if (min) {
      nextValue = Math.max(min, nextValue);
    }

    if (max) {
      nextValue = Math.min(max, nextValue);
    }

    if (onBlur) {
      onBlur();
    }

    setLastValue(formatNumber(nextValue, step));
    if (onChange) {
      onChange(nextValue);
      return;
    }

    logMissingOnChange('NumberInput', name);
  }

  const getNextValue = (multiplier = 1): number => {
    const currentTextValue = textValue;
    const countedStep = multiplier * step;
    return isValidNumber(currentTextValue) ? Number(currentTextValue) + countedStep : NaN;
  };

  const getDecrementedValue = (): number => getNextValue(-1);
  const canDecrement = (): boolean => !isNaN(getDecrementedValue()) && (isNil(min) || getDecrementedValue() >= min);
  const canIncrement = (): boolean => !isNaN(getNextValue()) && (isNil(max) || getNextValue() <= max);

  if(textValue !== lastValue) {
    setLastValue(textValue);
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const text = event.target.value;
    const value = text === '' ? null: isValidNumber(text) ? Number(text) : NaN;

    setLastValue(text);

    if (onChange) {
      onChange(value);
      return;
    }

    logMissingOnChange('NumberInput', name);
  };

  const endAdornmentWithArrows = <InputEndAdornment canDecrement={canDecrement()}
                                                    canIncrement={canIncrement()}
                                                    updateValue={updateNextValue}
                                                    endAdornment={endAdornment}
                                                    showArrows={showArrows}
                                                    disabled={disabled}
  />;

  return (
    <NxGenericTextField className={className}
                        disabled={disabled}
                        error={error}
                        id={id}
                        onBlur={onBlur}
                        onChange={handleChange}
                        endAdornment={endAdornmentWithArrows}
                        label={label}
                        name={name}
                        type='text'
                        positionAbsoluteError={positionAbsoluteError}
                        required={required}
                        inputMode="numeric"
                        inputComponent={inputComponent}
                        value={textValue}
                        inputProps={{'data-test': name}} />
  );
};

export default NxNumberInput;
